'use client';

/**
 * Dependency imports
 */
import React, { JSX } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import TransitionLink from './TransitionLink';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled('span', {
  shouldForwardProp: (prop: PropertyKey) =>
    ![
      'size',
      'variant',
      'color',
      'radius',
      'hasArrow',
      'reverseArrow',
      'expandOnHover',
      'fullwidth',
      'disabled',
      'loading',
    ].includes(prop.toString()),
})<Partial<ButtonCombinedProps>>(
  ({
    size = 'medium',
    variant = 'filled',
    color = 'dark',
    radius = 'xl',
    hasArrow = false,
    reverseArrow = false,
    expandOnHover = true,
    fullwidth = false,
    disabled = false,
  }) => css`
    position: relative;
    z-index: 0;
    display: inline-flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    border: none;
    background: none;
    border-radius: 99rem;

    border-radius: ${{
      xs: '0.25rem',
      sm: '0.5rem',
      md: '0.75rem',
      lg: '1rem',
      xl: '2rem',
    }[radius]};

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background-color: transparent;
      transition: background-color 200ms, width 200ms, left 200ms, opacity 200ms;
    }

    ${expandOnHover &&
    css`
      &:hover {
        &:before {
          width: ${{
        small: 'calc(100% + 0.4rem)',
        medium: 'calc(100% + 0.6rem)',
        large: 'calc(100% + 0.8rem)',
      }[size]};
          left: -0.5rem;
          left: ${{
        small: '-0.2rem',
        medium: '-0.3rem',
        large: '-0.4rem',
      }[size]};
        }
      }
    `}

    ${hasArrow &&
    !reverseArrow &&
    css`
      &:after {
        content: '';
        display: block;
        width: 0.4rem;
        height: 0.4rem;
        margin-inline-start: 0.5rem;
        border-top: 1.5px solid;
        border-inline-end: 1.5px solid;
        transform: rotate(45deg);
      }
    `}

    ${hasArrow &&
    reverseArrow &&
    css`
      &:after {
        content: '';
        display: block;
        width: 0.4rem;
        height: 0.4rem;
        order: -1;
        margin-inline-end: 0.5rem;
        border-top: 1.5px solid;
        border-inline-start: 1.5px solid;
        transform: rotate(-45deg);
      }
    `}

    ${fullwidth &&
    css`
      width: 100%;
      justify-content: center;
    `}

    padding: ${{
      small: '0.5rem 1.25rem',
      medium: '0.825rem 1.75rem',
      large: '1rem 2rem',
    }[size]};

    font-size: ${{
      small: '0.875rem',
      medium: '1rem',
      large: '1.125rem',
    }[size]};

    font-weight: ${{
      small: 500,
      medium: 500,
      large: 500,
    }[size]};

    ${variant === 'filled' && css`
      color: ${{
        inherit: 'inherit',
        default: 'inherit',
        primary: '#222',
        dark: '#fff',  // White text
        white: '#222',
        red: '#fff',
      }[color]};
    
      &:before {
        background-color: ${{
        inherit: 'inherit',
        default: '#e9e9e9',
        primary: 'var(--color-primary)',
        dark: '#4A4A4A',  // Consistent grey color
        white: '#fff',
        red: '#f66',
      }[color]};
      }
    
      &:hover {
        &:before {
          background-color: ${{
        inherit: 'inherit',
        default: '#f1f1f1',
        primary: 'var(--color-primary-light)',
        dark: '#FFC62E',  // Consistent yellow hover
        white: '#f0f0f0',
        red: '#f88',
      }[color]};
        }
    
        color: ${{
        inherit: 'inherit',
        default: 'inherit',
        primary: '#222',
        dark: '#222',  // Dark text on yellow background
        white: '#222',
        red: '#fff',
      }[color]};
      }
    
      &:active {
        &:before {
          background-color: ${{
        inherit: 'inherit',
        default: '#e6e6e6',
        primary: 'var(--color-primary-dark)',
        dark: '#E6B229',  // Consistent darker yellow for active
        white: '#e0e0e0',
        red: '#f44',
      }[color]};
        }
      }
    `}

    ${variant === 'subtle' &&
    css`
      color: ${{
        inherit: 'inherit',
        default: '#e9e9e9',
        primary: 'var(--color-primary)',
        dark: '#222',
        white: '#fff',
        red: '#f66',
      }[color]};

      &:before {
        opacity: 0.1;
        background-color: ${{
        inherit: 'inherit',
        default: '#e9e9e9',
        primary: 'var(--color-primary)',
        dark: '#222',
        white: '#fff',
        red: '#f66',
      }[color]};
      }

      &:hover {
        &:before {
          opacity: 0.16;
        }
      }

      &:active {
        &:before {
          opacity: 0.125;
        }
      }
    `}

    ${variant === 'outline' &&
    css`
      color: ${{
        inherit: 'inherit',
        default: 'inherit',
        primary: 'var(--color-primary)',
        dark: '#222',
        white: '#fff',
        red: '#f66',
      }[color]};

      &:before {
        border: 2px solid
          ${{
        inherit: 'currentColor',
        default: '#e9e9e9',
        primary: 'var(--color-primary)',
        dark: '#222',
        white: '#fff',
        red: '#f66',
      }[color]};
      }

      &:after {
        border-color: inherit;
      }
    `}

    ${variant === 'link' &&
    css`
      color: ${{
        inherit: 'inherit',
        default: 'inherit',
        primary: 'var(--color-primary)',
        dark: '#222',
        white: '#fff',
        red: '#f66',
      }[color]};

      &:after {
        border-color: inherit;
      }
    `}

    ${disabled &&
    css`
      cursor: default;
      pointer-events: none;
      opacity: 0.6;
    `}
  `
);

/**
 * Button Component
 */
export interface ButtonProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'filled' | 'link' | 'outline' | 'subtle';
  color?: 'default' | 'primary' | 'dark' | 'white' | 'red' | 'inherit';
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hasArrow?: boolean;
  reverseArrow?: boolean;
  expandOnHover?: boolean;
  fullwidth?: boolean;
  disabled?: boolean;
  href?: string;
  transition?: boolean;
  component?: 'a' | 'button';
  children?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  loading?: any;
  // Anchor-specific props
  target?: string;
  rel?: string;
  download?: string | boolean;
}

export type ButtonCombinedProps = ButtonProps & JSX.IntrinsicElements['span'];

const Button: React.FC<ButtonCombinedProps> = ({
  href = '#',
  transition = true,
  component = 'a',
  children,
  leftSection,
  rightSection,
  target,
  rel,
  download,
  ...props
}) => {
  const ButtonRoot = Root.withComponent('button');

  const buttonContent = (
    <>
      {leftSection && <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>{leftSection}</span>}
      {children}
      {rightSection && <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>{rightSection}</span>}
    </>
  );

  if (component === 'button') {
    return (
      <TransitionLink
        href={href}
        transition={transition}
        noLink
        style={{ pointerEvents: props.disabled ? 'none' : 'auto' }}
      >
        <ButtonRoot {...(props as any)}>{buttonContent}</ButtonRoot>
      </TransitionLink>
    );
  }

  return (
    <TransitionLink
      href={href}
      transition={transition}
      target={target}
      rel={rel}
      download={download}
      style={{ pointerEvents: props.disabled ? 'none' : 'auto' }}
    >
      <Root {...props}>{buttonContent}</Root>
    </TransitionLink>
  );
};

export default Button;
